import { useState } from "react";
import { ConfigProvider, Drawer, Menu } from "antd";
import type { MenuProps } from "antd";
import {
  House,
  Dumbbell,
  RotateCcw,
  ClipboardCheck,
  LayoutGrid,
  type LucideIcon,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import useDueCount from "../../../core/hooks/useDueCount";
import useUsuario from "../../../core/hooks/UseUsuario";
import useNotifications from "../../../core/hooks/useNotifications";
import {
  getSidebarNavGroups,
  toAntdMenuItem,
  SIDEBAR_MENU_THEME,
} from "./sidebarNavConfig";

export interface BottomTabBarProps {
  onMaisClick?: () => void;
}

interface BottomTab {
  key: string;
  label: string;
  icon: LucideIcon;
  isActive: (pathname: string) => boolean;
  onClick: () => void;
  badge?: number;
}

// Sidebar nav items already represented by their own bottom tab — kept out of
// the "Mais" drawer so nothing is listed twice. "7" is the Simulado submenu's
// key (Agenda/Periódico/Competitivo/Novo/Cadastrados/Histórico/Ajuda) — the
// Simulado tab stands in for that whole submenu, same as Home/Treino/Revisão
// stand in for their single-route entries.
const TAB_COVERED_KEYS = new Set(["/home", "/carrossel", "/revisao", "7"]);

export default function BottomTabBar({ onMaisClick }: BottomTabBarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { usuario } = useUsuario();

  // Same useDueCount/useNotifications data NotificationBell and Sidebar
  // already read — mounting them here doesn't add a request, see
  // Sidebar.tsx's note above its own usage.
  const dueToday = useDueCount();
  const { moderation } = useNotifications();
  const moderationCount = moderation
    ? moderation.pendingComments + moderation.openReports
    : 0;

  const [maisOpen, setMaisOpen] = useState(false);
  const closeMais = () => setMaisOpen(false);
  const handleMaisClick = () => {
    setMaisOpen(true);
    onMaisClick?.();
  };

  const maisGroups = getSidebarNavGroups({
    profile: usuario?.profile,
    navigate,
    moderationCount,
    // Nenhuma superfície móvel monta o SidebarUserCard, então perfil e
    // saída de sessão precisam vir como itens de menu — no rail do desktop
    // é o card que os oferece.
    includeAccountItems: true,
  })
    .map((group) =>
      group.key === "estudar"
        ? { ...group, items: group.items.filter((item) => !TAB_COVERED_KEYS.has(item.key)) }
        : group
    )
    .filter((group) => group.items.length > 0);

  const maisMenuItems: NonNullable<MenuProps["items"]> = maisGroups.map((group) => ({
    key: group.key,
    type: "group" as const,
    label: group.label,
    children: group.items.map(toAntdMenuItem),
  }));

  const tabs: BottomTab[] = [
    {
      key: "home",
      label: "Home",
      icon: House,
      isActive: (pathname) => pathname === "/home",
      onClick: () => navigate("/home"),
    },
    {
      key: "treino",
      label: "Treino",
      icon: Dumbbell,
      isActive: (pathname) => pathname.startsWith("/carrossel"),
      onClick: () => navigate("/carrossel"),
    },
    {
      key: "revisao",
      label: "Revisão",
      icon: RotateCcw,
      isActive: (pathname) => pathname.startsWith("/revisao"),
      onClick: () => navigate("/revisao"),
      badge: dueToday > 0 ? dueToday : undefined,
    },
    {
      key: "simulado",
      label: "Simulado",
      icon: ClipboardCheck,
      isActive: (pathname) =>
        pathname.startsWith("/agenda") ||
        pathname.startsWith("/inscricao") ||
        pathname.startsWith("/historico-simulado"),
      onClick: () => navigate("/agenda/completa", { state: "completa" }),
    },
    {
      key: "mais",
      label: "Mais",
      icon: LayoutGrid,
      isActive: () => maisOpen,
      onClick: handleMaisClick,
    },
  ];

  return (
    <>
      <nav className="flex h-[72px] w-full shrink-0 items-center border-t border-edge bg-surface px-2 pt-2 pb-3.5">
        {tabs.map((tab) => {
          const active = tab.isActive(location.pathname);
          const Icon = tab.icon;

          return (
            <button
              key={tab.key}
              type="button"
              onClick={tab.onClick}
              aria-current={active ? "page" : undefined}
              aria-label={
                tab.badge ? `${tab.label}, ${tab.badge} pendentes` : undefined
              }
              className="flex flex-1 flex-col items-center gap-1"
            >
              <div
                className={`relative flex h-[30px] w-[52px] items-center justify-center rounded-[15px] ${
                  active ? "bg-select/[0.14]" : "bg-transparent"
                }`}
              >
                <Icon
                  size={20}
                  className={active ? "text-select-ink" : "text-ink-muted"}
                />
                {!!tab.badge && (
                  <span
                    data-testid={`bottom-tab-badge-${tab.key}`}
                    aria-hidden
                    className="absolute right-1 top-0 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-brand px-1 text-[10px] font-semibold leading-none text-brand-contrast"
                  >
                    {tab.badge}
                  </span>
                )}
              </div>
              <span
                className={`text-[11px] ${
                  active
                    ? "font-semibold text-select-ink"
                    : "font-medium text-ink-muted"
                }`}
              >
                {tab.label}
              </span>
            </button>
          );
        })}
      </nav>

      <Drawer
        placement="bottom"
        open={maisOpen}
        onClose={closeMais}
        title="Mais"
        height="75vh"
        rootClassName="acertei-mais-drawer"
      >
        <ConfigProvider theme={SIDEBAR_MENU_THEME}>
          <Menu
            mode="inline"
            theme="light"
            selectable={false}
            onClick={closeMais}
            items={maisMenuItems}
            style={{ background: "transparent", borderInlineEnd: 0 }}
          />
        </ConfigProvider>
      </Drawer>
    </>
  );
}
